import { useEffect, useMemo, useState } from 'react'
import styles from './Fireworks.module.css'

interface FireworksProps {
  /** Number of firework bursts */
  count?: number
  /** Duration of the animation in seconds */
  duration?: number
  /** Array of colors for the fireworks */
  colors?: string[]
  /** Whether the animation is active */
  active?: boolean
  /** Callback when animation completes */
  onComplete?: () => void
}

interface Burst {
  id: number
  x: number
  y: number
  delay: number
  color: string
  particles: Particle[]
}

interface Particle {
  id: number
  angle: number
  distance: number
  size: number
}

const DEFAULT_COLORS = [
  '#f472b6', // pink-400
  '#a78bfa', // violet-400
  '#60a5fa', // blue-400
  '#34d399', // emerald-400
  '#fbbf24', // amber-400
]

export function Fireworks({
  count = 5,
  duration = 2.5,
  colors = DEFAULT_COLORS,
  active = true,
  onComplete,
}: FireworksProps) {
  const [isVisible, setIsVisible] = useState(active)

  const bursts = useMemo<Burst[]>(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: 15 + Math.random() * 70,
      y: 15 + Math.random() * 50,
      delay: i * 0.4 + Math.random() * 0.3,
      color: colors[Math.floor(Math.random() * colors.length)],
      particles: Array.from({ length: 12 }, (_, j) => ({
        id: j,
        angle: (j * 30) + Math.random() * 15,
        distance: 40 + Math.random() * 60,
        size: 4 + Math.random() * 4,
      })),
    }))
  }, [count, colors])

  useEffect(() => {
    setIsVisible(active)
    if (active && onComplete) {
      const maxDelay = count * 0.4 + 0.3
      const timer = setTimeout(() => {
        onComplete()
      }, (duration + maxDelay + 0.5) * 1000)
      return () => clearTimeout(timer)
    }
  }, [active, duration, count, onComplete])

  if (!isVisible) return null

  return (
    <div className={styles.Fireworks} aria-hidden="true">
      {bursts.map((burst) => (
        <div
          key={burst.id}
          className={styles.burst}
          style={
            {
              '--burst-x': `${burst.x}%`,
              '--burst-y': `${burst.y}%`,
              '--burst-delay': `${burst.delay}s`,
              '--burst-duration': `${duration}s`,
              '--burst-color': burst.color,
            } as React.CSSProperties
          }
        >
          {burst.particles.map((particle) => (
            <div
              key={particle.id}
              className={styles.particle}
              style={
                {
                  '--particle-angle': `${particle.angle}deg`,
                  '--particle-distance': `${particle.distance}px`,
                  '--particle-size': `${particle.size}px`,
                } as React.CSSProperties
              }
            />
          ))}
          <div className={styles.glow} />
        </div>
      ))}
    </div>
  )
}
